import useAppStore from '$appStore'
import { RangeFilter } from '$constants'
import { Dayjs } from 'dayjs'
import { ReportsMetadata } from '$models'
import { setCompanyTimeZone } from 'src/components/common/helpers'

/** The report date range as it goes in the url. AngularJS copies these four to the next page. */
export function reportRangeSearchParams({
  startDateTime,
  endDateTime,
  dateRange,
  prevDateRange,
}: ReportsMetadata): Record<string, string> {
  return {
    start: startDateTime.format(),
    end: endDateTime.format(),
    dateRange: dateRange,
    prevDateRange: prevDateRange,
  }
}

/**
 * A url value as a range filter, or the fallback when it is missing or unknown. The page
 * header draws no buttons for a range it does not know, leaving no way to change it.
 */
function toRangeFilter(
  value: string | undefined,
  fallback: RangeFilter
): RangeFilter {
  return Object.values(RangeFilter).includes(value as RangeFilter)
    ? (value as RangeFilter)
    : fallback
}

/**
 * Whether two ranges cover the same period. The previous filter is ignored, it only picks
 * which arrow buttons the header draws.
 */
export function isSameReportRange(
  a: ReportsMetadata,
  b: ReportsMetadata
): boolean {
  return (
    a.dateRange === b.dateRange &&
    a.startDateTime.isSame(b.startDateTime) &&
    a.endDateTime.isSame(b.endDateTime)
  )
}

/**
 * Gets the date range depending on query string params or if it doesn't have query
 * string params then it gets the values from the AppStore
 * @param queryParams Query string values from the URL
 * @returns startDateTime: Dayjs, endDateTime: Dayjs
 */
export function getDateRangeFromQueryStringOrAppStore(queryParams: {
  [key: string]: string
}) {
  const { start, end, dateRange, prevDateRange } = queryParams

  let startDateTime: Dayjs
  let endDateTime: Dayjs

  if (queryParams.start && queryParams.end && queryParams.dateRange) {
    startDateTime = setCompanyTimeZone(start)
    endDateTime = setCompanyTimeZone(end)
    const range = toRangeFilter(dateRange, RangeFilter.Day)
    const prevRange = toRangeFilter(prevDateRange, range)

    const {
      reportsMetadata: {
        startDateTime: storedStartDateTime,
        endDateTime: storedEndDateTime,
        dateRange: storedRange,
        prevDateRange: storedPrevRange,
      },
    } = useAppStore.getState()

    if (
      !storedEndDateTime.isSame(endDateTime) ||
      !storedStartDateTime.isSame(startDateTime) ||
      storedRange !== range ||
      storedPrevRange !== prevRange
    ) {
      useAppStore.setState({
        reportsMetadata: {
          startDateTime: startDateTime,
          endDateTime: endDateTime,
          dateRange: range,
          prevDateRange: prevRange,
        },
      })
    }
  } else {
    const { reportsMetadata, defaultReportsMetadata } = useAppStore.getState()

    // No range in the url means nothing was picked, so the page goes back to its default.
    // Holding the last pick instead is what leaves browser Back unable to reach a url
    // without one: the page would keep reading the old range and write it straight back.
    if (!isSameReportRange(reportsMetadata, defaultReportsMetadata)) {
      useAppStore.setState({ reportsMetadata: defaultReportsMetadata })
    }

    startDateTime = defaultReportsMetadata.startDateTime
    endDateTime = defaultReportsMetadata.endDateTime
  }

  return { startDateTime, endDateTime }
}
